import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Mic, Plus, Loader2, LogOut } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { RichTextEditor } from '@/components/RichTextEditor';
import { useDeepgram } from '@/hooks/useDeepgram';
import axios from 'axios';
import qs from 'qs';
import { API_BASE_URL, DEEPGRAM_API_KEY } from '@/config/api';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy } from '@dnd-kit/sortable';
import { SortableCard } from '@/components/SortableCard';

interface Section {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  editable?: boolean;
  canDelete?: boolean;
  prompt?: string;
  temperature?: number;
  order?: number;
}
interface TemplateCards {
  id?: string;
  sections: Section[];
  DSL?: string;
  DSL_JSON?: any;
}
const Onboarding = () => {
  const navigate = useNavigate();
  const {
    t,
    i18n
  } = useTranslation();
  const {
    user,
    updateUser,
    loading,
    logout,
    getUser
  } = useAuth();
  const {
    toast
  } = useToast();

  // State variables
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [templateCards, setTemplateCards] = useState<TemplateCards>({
    sections: []
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedMicrophone, setSelectedMicrophone] = useState('');
  const [availableMicrophones, setAvailableMicrophones] = useState([]);
  const [isProcessingContinue, setIsProcessingContinue] = useState(false);
  const [isContinueLoading, setIsContinueLoading] = useState(false);

  // Dialog states
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  // Edit/Add card states
  const [editingCard, setEditingCard] = useState<Section | null>(null);
  const [cardToDelete, setCardToDelete] = useState<Section | null>(null);
  const [editPrompt, setEditPrompt] = useState('');
  const [editTemperature, setEditTemperature] = useState(0.5);

  // Add new card states
  const [newCardTitle, setNewCardTitle] = useState('');
  const [newCardDescription, setNewCardDescription] = useState('');
  const [newCardPrompt, setNewCardPrompt] = useState('');
  const [newCardTemperature, setNewCardTemperature] = useState(0.5);
  const [addSectionError, setAddSectionError] = useState('');

  // Use the Deepgram hook
  const {
    startRecording,
    stopRecording,
    isRecording,
    transcriptionText,
    clearTranscription,
    connectionStatus,
    error: deepgramError
  } = useDeepgram({
    apiKey: DEEPGRAM_API_KEY,
    onTranscriptionReceived: transcriptionData => {
      console.log('✅ Transcription received:', transcriptionData.text);
    },
    onError: error => {
      console.error('❌ Deepgram error:', error);
      toast({
        title: 'Error de Transcripción',
        description: error,
        variant: "destructive"
      });
    }
  });

  // DndKit sensors
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8
    }
  }), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));

  // Ref to track if we've already initialized
  const initializedRef = useRef(false);
  console.log('🔄 Onboarding render - recording:', isRecording, 'status:', connectionStatus);

  // Single useEffect for initialization
  useEffect(() => {
    if (loading) {
      console.log('Still loading user...');
      return;
    }
    if (!user) {
      console.log('No user found, redirecting to home');
      navigate('/');
      return;
    }
    if (initializedRef.current) {
      console.log('Already initialized, skipping...');
      return;
    }
    console.log('Initializing Onboarding component...');
    initializedRef.current = true;

    // Set default specialty from user if available and load template
    if (user.specialty) {
      console.log('Setting user specialty:', user.specialty);
      setSelectedSpecialty(user.specialty);
      // Load template for user's specialty
      loadTemplateForSpecialty(user.specialty);
    } else {
      // Load user templates if no specialty set
      loadUserTemplates();
    }
    getMicrophones();
  }, [user, loading, navigate]);

  // Auto-start recording when dialog opens
  useEffect(() => {
    if (isDialogOpen && selectedMicrophone && !isRecording && connectionStatus === 'disconnected') {
      console.log('🚀 Auto-starting recording when dialog opens...');
      // Small delay to ensure dialog is fully rendered
      const timer = setTimeout(() => {
        clearTranscription();
        startRecording();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isDialogOpen, selectedMicrophone, isRecording, connectionStatus, startRecording, clearTranscription]);

  // If still loading, show loading spinner
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>;
  }

  // If no user after loading, redirect
  if (!user) {
    return null;
  }
  const getMicrophones = async () => {
    try {
      console.log('Requesting microphone permissions...');
      await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphones = devices.filter(device => device.kind === 'audioinput');
      console.log('Available microphones:', microphones);
      setAvailableMicrophones(microphones);
      if (microphones.length > 0 && !selectedMicrophone) {
        setSelectedMicrophone(microphones[0].deviceId);
        console.log('Default microphone selected:', microphones[0].deviceId);
      }
    } catch (error) {
      console.error("Error al obtener micrófonos o permisos:", error);
      toast({
        title: 'Error',
        description: 'No se pudo acceder a los micrófonos. Verifica los permisos.',
        variant: "destructive"
      });
    }
  };
  const loadUserTemplates = async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/templates`, {
        params: {
          'filters[user][id][$eq]': user.id,
          'populate[sections][locale]': i18n.language
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.data && response.data.data && response.data.data[0]) {
        const userTemplate = response.data.data[0];
        userTemplate.attributes.id = userTemplate.id;
        setTemplateCards(userTemplate.attributes);
        if (userTemplate.attributes.specialty) {
          setSelectedSpecialty(userTemplate.attributes.specialty);
        }
      }
    } catch (error) {
      console.error("Error al cargar los templates del usuario:", error);
    }
  };
  const loadTemplateForSpecialty = async (specialty: string) => {
    try {
      console.log('Loading template for specialty:', specialty);

      // First try to get user template
      const userTemplateResponse = await axios.get(`${API_BASE_URL}/templates`, {
        params: {
          'filters[user][id][$eq]': user?.id,
          'filters[specialty][$eq]': specialty,
          'populate[sections][locale]': i18n.language
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (userTemplateResponse.data && userTemplateResponse.data.data && userTemplateResponse.data.data[0]) {
        const userTemplate = userTemplateResponse.data.data[0];
        userTemplate.attributes.id = userTemplate.id;
        setTemplateCards(userTemplate.attributes);
        console.log('Loaded user template:', userTemplate.attributes);
        return;
      }

      // If no user template, get system template
      const systemTemplateResponse = await axios.get(`${API_BASE_URL}/templates`, {
        params: {
          'filters[specialty][$eq]': specialty,
          'filters[system][$eq]': true,
          locale: i18n.language,
          'populate[sections][locale]': i18n.language
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (systemTemplateResponse.data && systemTemplateResponse.data.data && systemTemplateResponse.data.data[0]) {
        const systemTemplate = systemTemplateResponse.data.data[0];
        systemTemplate.attributes.id = systemTemplate.id;
        setTemplateCards(systemTemplate.attributes);
        console.log('Loaded system template:', systemTemplate.attributes);
      } else {
        console.log('No template found for specialty:', specialty);
        setTemplateCards({
          sections: []
        });
      }
    } catch (error) {
      console.error('Error loading template for specialty:', specialty, error);
      setTemplateCards({
        sections: []
      });
    }
  };
  const saveUserTemplate = async (specialty: string, sections: Section[]) => {
    if (!user) return;
    try {
      console.log('Saving user template for specialty:', specialty);

      // First, clean up user from other specialty templates
      const userTemplatesQuery = qs.stringify({
        locale: 'all',
        filters: {
          user: {
            id: {
              $eq: user.id
            }
          }
        }
      }, {
        encodeValuesOnly: true
      });
      const userTemplatesResponse = await axios.get(`${API_BASE_URL}/templates?${userTemplatesQuery}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Remove user from templates of other specialties
      if (userTemplatesResponse.data && userTemplatesResponse.data.data) {
        const userTemplates = userTemplatesResponse.data.data;
        for (const template of userTemplates) {
          if (template.attributes.specialty !== specialty) {
            const templatePayload = {
              data: {
                user: null
              }
            };
            await axios.put(`${API_BASE_URL}/templates/${template.id}?locale=all`, templatePayload, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });
            console.log(`Removed user from template ID: ${template.id}`);
          }
        }
      }

      // Get system template to copy DSL and DSL_JSON
      const systemTemplateQuery = qs.stringify({
        locale: i18n.language,
        filters: {
          specialty: {
            $eq: specialty
          },
          system: {
            $eq: true
          }
        },
        populate: {
          'sections': {
            locale: i18n.language
          }
        }
      }, {
        encodeValuesOnly: true
      });
      const systemTemplateResponse = await axios.get(`${API_BASE_URL}/templates?${systemTemplateQuery}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      let systemTemplate = null;
      if (systemTemplateResponse.data && systemTemplateResponse.data.data && systemTemplateResponse.data.data[0]) {
        systemTemplate = systemTemplateResponse.data.data[0];
      }

      // Clean sections (remove id and __component)
      const cleanedSections = sections.map((section, index) => {
        const {
          id,
          __component,
          ...cleanedSection
        } = section as any;
        return {
          ...cleanedSection,
          order: index
        };
      });
      const userTemplatePayload = {
        data: {
          specialty: specialty,
          system: false,
          user: user.id,
          sections: cleanedSections,
          locale: i18n.language,
          DSL: systemTemplate?.attributes.DSL || null,
          DSL_JSON: systemTemplate?.attributes.DSL_JSON || null
        }
      };

      // Check if user template for this specialty already exists
      const userTemplateQuery = qs.stringify({
        locale: 'all',
        filters: {
          user: {
            id: {
              $eq: user.id
            }
          },
          specialty: {
            $eq: specialty
          }
        }
      }, {
        encodeValuesOnly: true
      });
      const existingUserTemplateResponse = await axios.get(`${API_BASE_URL}/templates?${userTemplateQuery}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (existingUserTemplateResponse.data && existingUserTemplateResponse.data.data && existingUserTemplateResponse.data.data[0]) {
        // Update existing template
        const existingTemplateId = existingUserTemplateResponse.data.data[0].id;
        await axios.put(`${API_BASE_URL}/templates/${existingTemplateId}?locale=all&populate=*`, userTemplatePayload, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        console.log('Updated existing user template for specialty:', specialty);
      } else {
        // Create new template
        await axios.post(`${API_BASE_URL}/templates?locale=all&populate=*`, userTemplatePayload, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        console.log('Created new user template for specialty:', specialty);
      }
    } catch (error) {
      console.error('Error saving user template:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar el template. Inténtalo de nuevo.',
        variant: "destructive"
      });
    }
  };
  const handleSpecialtyChange = async (specialty: string) => {
    console.log('Specialty changed to:', specialty);
    setSelectedSpecialty(specialty);
    await loadTemplateForSpecialty(specialty);

    // Save the template for the user after loading
    if (templateCards.sections.length > 0) {
      await saveUserTemplate(specialty, templateCards.sections);
    }
  };
  const handleMicrophoneChange = (deviceId: string) => {
    console.log('Microphone changed to:', deviceId);
    setSelectedMicrophone(deviceId);
  };
  const toggleRecording = async () => {
    console.log('🎯 Toggle recording clicked');
    console.log('Current state - recording:', isRecording, 'status:', connectionStatus);
    if (isRecording || connectionStatus === 'recording' || connectionStatus === 'connected') {
      console.log('🛑 Stopping recording...');
      stopRecording();
      return; // Exit early to prevent restart
    }
    if (connectionStatus === 'disconnected') {
      console.log('▶️ Starting recording...');
      clearTranscription();
      await startRecording();
    }
  };
  const handleDragEnd = (event: any) => {
    const {
      active,
      over
    } = event;
    if (active.id !== over.id) {
      setTemplateCards(current => {
        const oldIndex = current.sections.findIndex(item => item.id === active.id);
        const newIndex = current.sections.findIndex(item => item.id === over.id);
        const updatedSections = arrayMove(current.sections, oldIndex, newIndex).map((item, idx) => ({
          ...item,
          order: idx
        }));
        return {
          ...current,
          sections: updatedSections
        };
      });
    }
  };
  const handleEditCard = (card: Section) => {
    setEditingCard(card);
    setEditPrompt(card.prompt || '');
    setEditTemperature(card.temperature || 0.5);
    setIsEditDialogOpen(true);
  };
  const handleDeleteCard = (card: Section) => {
    setCardToDelete(card);
    setIsDeleteDialogOpen(true);
  };
  const confirmDeleteCard = () => {
    if (cardToDelete) {
      const updatedSections = templateCards.sections.filter(section => section.id !== cardToDelete.id);
      setTemplateCards({
        ...templateCards,
        sections: updatedSections
      });
      setIsDeleteDialogOpen(false);
      setCardToDelete(null);
    }
  };
  const confirmEditCard = () => {
    if (editingCard) {
      const updatedSections = templateCards.sections.map(section => section.id === editingCard.id ? {
        ...section,
        prompt: editPrompt,
        temperature: editTemperature
      } : section);
      setTemplateCards({
        ...templateCards,
        sections: updatedSections
      });
      setIsEditDialogOpen(false);
      setEditingCard(null);
    }
  };
  const handleAddCard = () => {
    setNewCardTitle('');
    setNewCardDescription('');
    setNewCardPrompt('');
    setNewCardTemperature(0.5);
    setAddSectionError('');
    setIsAddDialogOpen(true);
  };
  const confirmAddCard = () => {
    if (!newCardTitle.trim() || !newCardDescription.trim()) {
      setAddSectionError('Todos los campos son requeridos');
      return;
    }
    const newCard: Section = {
      id: Date.now().toString(),
      title: newCardTitle,
      description: newCardDescription,
      prompt: newCardPrompt,
      temperature: newCardTemperature,
      checked: true,
      editable: true,
      canDelete: true
    };
    setTemplateCards({
      ...templateCards,
      sections: [...templateCards.sections, newCard]
    });
    setIsAddDialogOpen(false);
    setNewCardTitle('');
    setNewCardDescription('');
    setNewCardPrompt('');
    setNewCardTemperature(0.5);
    setAddSectionError('');
  };
  const handleContinue = async () => {
    console.log('🚀 handleContinue started');
    console.log('📊 Current user state:', {
      id: user?.id,
      first_use: user?.first_use,
      specialty: user?.specialty,
      selectedSpecialty,
      selectedMicrophone
    });
    if (!selectedMicrophone) {
      toast({
        title: t('error'),
        description: 'Por favor selecciona un micrófono antes de continuar.',
        variant: "destructive"
      });
      return;
    }
    setIsContinueLoading(true);
    setIsProcessingContinue(true);
    try {
      // Force stop recording immediately when processing starts
      if (isRecording) {
        console.log('🛑 Forcing stop recording at start of processing...');
        stopRecording();
        // Wait for recording to fully stop
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Additional wait for connection to fully close
      await new Promise(resolve => setTimeout(resolve, 500));

      // Prepare all user updates in a single object
      const updateData: any = {
        microphone: selectedMicrophone,
        first_use: false
      };
      if (selectedSpecialty) {
        updateData.specialty = selectedSpecialty;
      }
      console.log('🔄 Updating user with data:', updateData);

      // Single API call to update all user data
      const response = await axios.put(`${API_BASE_URL}/users/${user?.id}`, updateData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      console.log('✅ User update response:', response.data);

      // Update local user state immediately
      updateUser(updateData);

      // Force refresh user data from server to ensure synchronization
      console.log('🔄 Forcing user data refresh from server...');
      await getUser();

      // Save final template configuration only if specialty is selected
      if (selectedSpecialty && templateCards.sections.length > 0) {
        console.log('💾 Saving user template...');
        await saveUserTemplate(selectedSpecialty, templateCards.sections);
      }

      // Set flag in sessionStorage to indicate we're coming from onboarding
      console.log('🏷️ Setting onboarding completion flag...');
      sessionStorage.setItem('onboarding_completed', 'true');
      console.log('🚀 Onboarding complete, navigating to dashboard...');
      console.log('📊 Final user state before navigation:', {
        id: user?.id,
        first_use: user?.first_use,
        specialty: user?.specialty || selectedSpecialty
      });

      // Wait a moment for all state updates to complete
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Navigate to dashboard with replace to prevent going back
      navigate('/dashboard', {
        replace: true
      });
    } catch (error) {
      console.error('❌ Error al procesar la configuración:', error);
      toast({
        title: t('error'),
        description: t('unexpected_error'),
        variant: "destructive"
      });
    } finally {
      // Always reset loading states in finally block
      setIsContinueLoading(false);
      setIsProcessingContinue(false);
    }
  };
  const handleSaveAndStart = () => {
    // Simplemente abrir el diálogo para configuración del micrófono
    setIsDialogOpen(true);
  };
  const handleDialogClose = (open: boolean) => {
    console.log('🚪 Dialog close requested - open:', open, 'recording:', isRecording);

    // Solo permitir cerrar el diálogo si no estamos procesando
    if (!open && !isProcessingContinue) {
      console.log('🛑 Closing dialog - forcing stop recording...');
      stopRecording();
      // Clear transcription text when dialog closes
      clearTranscription();
      setIsDialogOpen(false);
    } else if (!open && isProcessingContinue) {
      console.log('🚫 Cannot close dialog while processing...');
      // No cerrar el diálogo mientras se está procesando
      return;
    } else {
      setIsDialogOpen(open);
    }
  };

  // Helper function to get status text with translations
  const getStatusText = () => {
    // Don't show any status when processing continue
    if (isProcessingContinue) {
      return '';
    }

    // Only show status when dialog is open AND recording is active
    if (!isDialogOpen || !isRecording) {
      return '';
    }
    switch (connectionStatus) {
      case 'connecting':
        return t('connecting');
      case 'connected':
        return t('connected');
      case 'recording':
        return t('recording');
      case 'disconnected':
        return '';
      case 'error':
        return t('connection_error');
      default:
        return '';
    }
  };
  console.log('Rendering Onboarding component, user exists:', !!user);
  if (!user) {
    console.log('No user, rendering null');
    return null;
  }
  return <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      <div className="flex flex-col lg:flex-row h-screen bg-white overflow-hidden">
        {/* Sidebar - Hidden on Mobile */}
        <aside className="hidden lg:block lg:w-1/4 bg-gray-50 p-6 shadow-lg overflow-y-auto">
          <div className="mb-6">
            <div className="rounded-lg mb-4 flex items-center justify-start overflow-hidden">
              <img  src="/logo.png" alt="Clara Logo" className="w-[230px] h-[100px] object-contain" />
            </div>
            <h2 className="text-2xl font-bold">{t('welcome_to_clara')}</h2>
            {user && <p className="text-lg text-muted-foreground">{user.first_name} {user.last_name}</p>}
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            {t('configure_report_sections')}
          </p>
          <div className="flex space-x-1 mb-4">
            <div className="w-full  bg-gradient-to-br from-primary/20 to-secondary/20 rounded-lg flex items-center justify-center overflow-hidden">
              <img src="/lovable-uploads/33dcca63-f3f3-465b-89f4-82aedf4123b3.png" alt="Informe y Transcripción" className="w-[230px] h-[100px] object-contain" />
            </div>
          </div>
        </aside>

        {/* Main Content - Full width on mobile */}
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
          <div className="flex flex-col mb-6">
            {/* Mobile header with buttons */}
            <div className="flex justify-between items-start mb-4 lg:hidden">
              <h2 className="text-lg font-semibold">{t('select_report_sections')}</h2>
              <div className="flex gap-2 ml-4">
                <Button variant="outline" size="sm" className="flex items-center" onClick={handleAddCard}>
                  <Plus className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" className="flex items-center" onClick={logout}>
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* Desktop header */}
            <div className="hidden lg:flex lg:justify-between lg:items-center">
              <h2 className="text-xl font-semibold">{t('select_report_sections')}</h2>
              <div className="flex gap-4">
                <Button variant="outline" className="flex items-center" onClick={handleAddCard}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('add_custom_section')}
                </Button>
                <Button variant="outline" className="flex items-center" onClick={logout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('logout')}
                </Button>
              </div>
            </div>
          </div>

          <div className="max-w-none lg:max-w-4xl mx-auto">
            <div className="mb-6 w-full lg:w-[300px]">
              <Label htmlFor="specialty-select">{t('select_specialty')} <span className="text-gray-400">(opcional)</span></Label>
              <Select value={selectedSpecialty} onValueChange={handleSpecialtyChange}>
                <SelectTrigger className="w-full mt-2">
                  <SelectValue placeholder={t('select_specialty_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pediatra">{t('pediatrician')}</SelectItem>
                  <SelectItem value="rehabilitador_oral">{t('oral_rehabilitator')}</SelectItem>
                  <SelectItem value="ginecologo">{t('gynecologist')}</SelectItem>
                  <SelectItem value="internista">{t('internist')}</SelectItem>
                  <SelectItem value="psicologo">{t('psychologist')}</SelectItem>
                  <SelectItem value="psiquiatra">{t('psychiatrist')}</SelectItem>
                  <SelectItem value="veterinaria">{t('veterinary')}</SelectItem>
                  <SelectItem value="glaucomatologo">{t('glaucomatologist')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="mb-6">
              {templateCards.sections && templateCards.sections.length > 0 && <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={templateCards.sections.map(card => card.id)} strategy={rectSortingStrategy}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {templateCards.sections.map(section => <SortableCard key={section.id} card={section} handleEditCard={handleEditCard} handleDeleteCard={handleDeleteCard} setTemplateCards={setTemplateCards} templateCards={templateCards} />)}
                    </div>
                  </SortableContext>
                </DndContext>}
            </div>

            <hr className="my-6 border-t border-gray-200" />
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mt-4 space-y-4 lg:space-y-0 pb-8 lg:pb-0">
              <p className="text-sm text-muted-foreground">
                {t('modify_settings_anytime')}
              </p>
              <Button onClick={handleSaveAndStart} className="bg-orange-500 hover:bg-orange-600 text-white w-full lg:w-auto">
                {t('save_and_start')}
              </Button>
            </div>
          </div>
        </main>

        {/* Microphone Configuration Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
          <DialogContent className="w-[95vw] max-w-[600px] mx-auto my-4 [&>button]:hidden" onPointerDownOutside={e => isProcessingContinue && e.preventDefault()} onEscapeKeyDown={e => isProcessingContinue && e.preventDefault()}>
            <DialogHeader>
              <DialogTitle className="text-xl lg:text-2xl font-bold text-primary">{t('microphone_setup')}</DialogTitle>
              <DialogDescription>
                {t('select_microphone_test')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="microphone-select">{t('select_microphone')}</Label>
                <RadioGroup value={selectedMicrophone} onValueChange={handleMicrophoneChange} className="space-y-2 mt-4">
                  {availableMicrophones.map((microphone: any) => <div key={microphone.deviceId} className="flex items-center space-x-2">
                      <RadioGroupItem value={microphone.deviceId} id={microphone.deviceId} />
                      <Label htmlFor={microphone.deviceId} className="text-sm">
                        {microphone.label || `${t('microphone')} ${microphone.deviceId.slice(0, 5)}`}
                      </Label>
                    </div>)}
                </RadioGroup>
              </div>

              <div>
                <Label htmlFor="transcription">{t('test_microphone')}</Label>
                <Textarea id="transcription" value={transcriptionText || ''} placeholder={t('click_microphone_speak_test')} className="h-24 resize-none mb-4 mt-2" readOnly />
              </div>

              {!isProcessingContinue && <div className="flex items-center space-x-4">
                  <Button variant={isRecording ? "destructive" : "default"} size="lg" className={`w-16 h-16 rounded-full ${isRecording ? 'animate-pulse' : ''}`} onClick={toggleRecording} disabled={connectionStatus === 'connecting'}>
                    <Mic className="w-8 h-8" />
                  </Button>
                  <div className="flex-1">
                    {getStatusText() && <p className="text-lg font-medium">
                        {getStatusText()}
                      </p>}
                    {deepgramError && isDialogOpen && isRecording && <p className="text-red-600 text-sm font-medium">Error: {deepgramError}</p>}
                  </div>
                </div>}

              {isProcessingContinue && <div className="flex items-center justify-center space-x-2 py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <p className="text-lg font-medium">{t('processing')}</p>
                </div>}
            </div>
            <DialogFooter>
              <Button onClick={handleContinue} className="bg-primary text-primary-foreground hover:bg-primary/90 w-full lg:w-auto" disabled={isContinueLoading}>
                {isContinueLoading ? <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('processing')}
                  </> : t('continue')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="w-[95vw] max-w-lg mx-auto">
            <DialogHeader>
              <DialogTitle>{t('confirm_delete_section')}</DialogTitle>
              <DialogDescription>
                {t('action_cannot_undone')}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex flex-col lg:flex-row space-y-2 lg:space-y-0 lg:space-x-2">
              <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)} className="w-full lg:w-auto">{t('cancel')}</Button>
              <Button variant="destructive" onClick={confirmDeleteCard} className="w-full lg:w-auto">{t('delete')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Card Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="w-[95vw] max-w-[800px] mx-auto">
            <DialogHeader>
              <DialogTitle>{t('edit')} {editingCard?.title}</DialogTitle>
              <DialogDescription>
                {t('modify_prompt_configuration')}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-1 gap-4">
                <Label htmlFor="prompt">{t('prompt')}</Label>
                <RichTextEditor value={editPrompt} onChange={setEditPrompt} placeholder={t('write_prompt_section')} className="h-32 resize-none" />
              </div>
            </div>
            <DialogFooter className="flex flex-col lg:flex-row space-y-2 lg:space-y-0 lg:space-x-2">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)} className="w-full lg:w-auto">{t('cancel')}</Button>
              <Button onClick={confirmEditCard} className="w-full lg:w-auto">{t('save_changes')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Card Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="w-[95vw] max-w-[600px] mx-auto">
            <DialogHeader>
              <DialogTitle>{t('add_new_section')}</DialogTitle>
            </DialogHeader>
            {addSectionError && <div className="text-red-500 text-sm mt-2">{addSectionError}</div>}
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-1 gap-4">
                <Label htmlFor="title">{t('title')}</Label>
                <Input id="title" value={newCardTitle} onChange={e => setNewCardTitle(e.target.value)} placeholder={t('section_title')} />
                <Label htmlFor="description">{t('description')}</Label>
                <Input id="description" value={newCardDescription} onChange={e => setNewCardDescription(e.target.value)} placeholder={t('section_description')} />
                <Label htmlFor="prompt">{t('prompt')}</Label>
                <RichTextEditor value={newCardPrompt} onChange={setNewCardPrompt} placeholder={t('write_prompt_section')} className="h-32 resize-none" />
              </div>
            </div>
            <DialogFooter className="flex flex-col lg:flex-row space-y-2 lg:space-y-0 lg:space-x-2">
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)} className="w-full lg:w-auto">{t('cancel')}</Button>
              <Button onClick={confirmAddCard} className="w-full lg:w-auto">{t('add')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>;
};

export default Onboarding;
